import React, { useCallback, useEffect, useRef, useState } from "react";
import "@/components/sections/Registration.css";
import { eventConfig } from "@/config/eventConfig";
import { fetchSeats } from "@/lib/registrationService";
import { Countdown } from "@/components/Countdown";
import RegistrationForm from "@/components/sections/RegistrationForm";
import { SeatsBanner, VenueCard } from "@/components/sections/RegistrationAside";
import { logger } from "@/lib/logger";

const Registration = () => {
  const { registration, event } = eventConfig;

  const rootRef = useRef(null);
  const pointerFrame = useRef(null);
  const [seats, setSeats] = useState(null);
  const [visible, setVisible] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);

  // Live seat availability
  const loadSeats = useCallback(async () => {
    try {
      const data = await fetchSeats(eventConfig.eventId);
      setSeats(data);
    } catch (e) {
      // Seat count is non-critical UI; keep the last known value.
      logger.debug("[registration] seat fetch failed", e);
    }
  }, []);

  useEffect(() => {
    loadSeats();
    const id = setInterval(loadSeats, 45000);
    return () => clearInterval(id);
  }, [loadSeats]);

  // Reveal on scroll
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      setCardVisible(true);
      return;
    }
    let timer;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setVisible(true);
          timer = setTimeout(() => setCardVisible(true), 130);
          observer.disconnect();
        });
      },
      { threshold: 0.16 }
    );
    observer.observe(root);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  const isRestricted = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    (window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(pointer: coarse)").matches);

  const onRootMove = (e) => {
    if (isRestricted()) return;
    const root = rootRef.current;
    if (!root) return;
    window.cancelAnimationFrame(pointerFrame.current);
    pointerFrame.current = window.requestAnimationFrame(() => {
      const rect = root.getBoundingClientRect();
      root.style.setProperty("--crl-pointer-x", `${e.clientX - rect.left}px`);
      root.style.setProperty("--crl-pointer-y", `${e.clientY - rect.top}px`);
    });
  };
  const onRootLeave = () => {
    const root = rootRef.current;
    if (!root) return;
    root.style.setProperty("--crl-pointer-x", "32%");
    root.style.setProperty("--crl-pointer-y", "42%");
  };

  return (
    <section
      ref={rootRef}
      className="cpf-registration-live has-js"
      id="register"
      aria-labelledby="cpf-registration-title"
      data-testid="registration-section"
      onPointerMove={onRootMove}
      onPointerLeave={onRootLeave}
    >
      <div className="crl-background" aria-hidden="true">
        <svg className="crl-network" viewBox="0 0 1440 900" preserveAspectRatio="none">
          <path d="M-40 700 C230 510, 330 675, 560 445 S940 230, 1480 420" />
          <path d="M-30 205 C280 395, 470 100, 730 320 S1090 720, 1480 600" />
          <path d="M160 930 C350 615, 620 755, 820 500 S1130 160, 1370 -50" />
          <circle cx="555" cy="448" r="4" />
          <circle cx="820" cy="500" r="4" />
          <circle cx="1120" cy="315" r="4" />
        </svg>
        <span className="crl-orbit crl-orbit--one" />
        <span className="crl-orbit crl-orbit--two" />
        <span className="crl-decision-node crl-decision-node--55"><small>Age</small><strong>55</strong></span>
        <span className="crl-decision-node crl-decision-node--65"><small>Age</small><strong>65</strong></span>
        <span className="crl-decision-node crl-decision-node--70"><small>Age</small><strong>70</strong></span>
        <i className="crl-particle crl-particle--1" />
        <i className="crl-particle crl-particle--2" />
        <i className="crl-particle crl-particle--3" />
        <i className="crl-particle crl-particle--4" />
      </div>

      <div className="crl-container">
        <div className="crl-grid">
          <div className={`crl-copy${visible ? " is-visible" : ""}`}>
            <p className="crl-eyebrow">Complimentary admission</p>
            <h2 id="cpf-registration-title">{registration.heading}</h2>
            <p className="crl-copy__intro">{registration.supporting}</p>

            <div className="crl-date-card">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="5" width="18" height="16" rx="3" />
                <path d="M8 3v4M16 3v4M3 10h18" />
              </svg>
              <span className="crl-date-card__copy">
                <strong>{event.dateLabel}</strong>
                <small>{event.timeLabel} · Singapore</small>
              </span>
            </div>

            <ul className="crl-benefits">
              {registration.inclusions.map((inc) => (
                <li key={inc}>{inc}</li>
              ))}
            </ul>

            <VenueCard event={event} />
          </div>

          <div className={`crl-form-col${cardVisible ? " is-visible" : ""}`}>
            <SeatsBanner seats={seats} />

            <div className="crl-countdown" data-testid="registration-countdown">
              <Countdown variant="block" label="Seminar starts in" />
            </div>

            <RegistrationForm onSuccess={loadSeats} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Registration;
